import React from 'react';
import { Tag, Heading, Lead, PageHero, Section, BtnPrimary, Card } from '../components/UI';
import styles from './Software.module.css';

const TYPES = [
  { t: 'Custom Software Solutions',  d: 'Purpose-built software tailored specifically to your business processes and goals.' },
  { t: 'Web Applications',            d: 'Scalable, browser-based applications with modern tech stacks.' },
  { t: 'Mobile Applications',         d: 'iOS, Android, and cross-platform apps for all devices.' },
  { t: 'Enterprise Software',         d: 'Large-scale systems handling high data volumes, users, and transactions.' },
  { t: 'SaaS Products',               d: 'Subscription-based cloud software designed for multi-tenant deployment.' },
  { t: 'E-commerce Platforms',        d: 'Complete online commerce solutions with payments, inventory, and CMS.' },
  { t: 'Cloud-Based Applications',    d: 'AWS, Azure, and Google Cloud native apps built for scalability.' },
];

const PROCESS = [
  { n: '01', t: 'Requirement Analysis', d: 'Deep discovery of your business needs, goals, and technical constraints.' },
  { n: '02', t: 'Planning & Strategy',  d: 'Architecture planning, tech stack selection, and project roadmap.' },
  { n: '03', t: 'UI/UX Design',         d: 'User-centered interface design with prototyping and validation.' },
  { n: '04', t: 'Development',          d: 'Agile sprint-based development with continuous integration.' },
  { n: '05', t: 'Testing & QA',         d: 'Comprehensive automated and manual testing across all layers.' },
  { n: '06', t: 'Deployment',           d: 'Smooth production deployment with zero-downtime strategies.' },
  { n: '07', t: 'Support & Scale',      d: 'Ongoing monitoring, updates, and performance optimization.' },
];

const TECH = [
  { label: 'Frontend',  items: ['HTML5', 'CSS3', 'JavaScript', 'React', 'Angular', 'Vue.js'],   dark: true },
  { label: 'Backend',   items: ['Node.js', 'Python', 'PHP', 'Java', 'Spring Boot', 'Django'],   dark: false },
  { label: 'Mobile',    items: ['Flutter', 'React Native', 'Swift', 'Kotlin'],                   dark: true },
  { label: 'Database',  items: ['MySQL', 'MongoDB', 'PostgreSQL', 'Redis', 'Elasticsearch'],     dark: false },
  { label: 'Cloud',     items: ['AWS', 'Azure', 'Google Cloud', 'Docker', 'Kubernetes'],         dark: true },
];

export default function Software() {
  return (
    <>
      <PageHero
        tag="Software Development"
        title="Transforming Ideas into Robust, Scalable Solutions"
        subtitle="In a rapidly evolving digital world, businesses need powerful and reliable software solutions. We deliver end-to-end solutions tailored to your unique requirements — ensuring performance, flexibility, and long-term success."
      />

      

      {/* ── Process ────────────────────────────────────────────── */}
      <Section variant="white">
        <Tag>Our Methodology</Tag>
        <Heading>Development Process</Heading>
        <Lead>A structured, proven approach that ensures quality delivery from day one.</Lead>
        <div className={styles.processGrid}>
          {PROCESS.map((step, i) => (
            <div key={i} className={styles.processStep}>
              <div className={styles.processNum}>{step.n}</div>
              <h4 className={styles.processTitle}>{step.t}</h4>
              <p className={styles.processDesc}>{step.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Tech Stack ─────────────────────────────────────────── */}
      <Section variant="ink">
        <Tag light>Technologies</Tag>
        <Heading light>Our Tech Stack</Heading>
        <Lead light>We work with the tools and frameworks that best fit your project's needs.</Lead>
        <div className={styles.techStack}>
          {TECH.map((row, i) => (
            <div key={i} className={styles.techRow}>
              <div className={styles.techLabel}>{row.label}</div>
              <div className={styles.techItems}>
                {row.items.map((item, j) => (
                  <span key={j} className={`${styles.techBadge} ${row.dark ? styles.techBadgeDark : styles.techBadgeOutline}`}>
                    {item}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Why Choose Our Dev ─────────────────────────────────── */}
      <Section variant="paper">
        <div className={styles.bottomGrid}>
          <div>
            <Tag>Why Choose Us</Tag>
            <Heading>Built for Performance & Scale</Heading>
            <Lead>Every solution we build is crafted with long-term success in mind.</Lead>
            <ul className={styles.benefitList}>
              {[
                'Experienced Development Team across all platforms',
                'Scalable & Secure architecture by design',
                'Agile methodology for faster, adaptive delivery',
                'On-Time Delivery with milestone tracking',
                'Cost-Effective Solutions without quality compromise',
                '24/7 Post-launch Support & Maintenance',
              ].map((item, i) => (
                <li key={i} className={styles.benefitItem}>
                  <span className={styles.benefitDot} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className={styles.industriesServed}>
            <div className={styles.indCard}>
              <h4 className={styles.indTitle}>Industries We Serve</h4>
              {['Healthcare', 'E-commerce', 'Education', 'Finance', 'Logistics', 'Real Estate'].map((ind, i) => (
                <div key={i} className={styles.indItem}>{ind}</div>
              ))}
            </div>
            <BtnPrimary to="/contact" style={{ marginTop: '1.5rem', width: '100%', justifyContent: 'center' }}>
              Start Your Project →
            </BtnPrimary>
          </div>
        </div>
      </Section>
    </>
  );
}
